import { useContext, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { Checkbox, Input, Search } from "../../ui";
import { getCampaignText } from "./campaignActionsText";
import StatusBadge from "./StatusBadge";
import "./CampaignActions.css";

// Searchable checkbox list with "select all" and a selected count, for
// choosing restaurants (or requests). items: [{ id, label, hint, status }].
// "Select all" applies to the items matching the current search.
export default function MultiSelectList({ items, selectedIds, onChange, disabled = false, label, emptyText }) {
  const { lang } = useContext(Context);
  const text = getCampaignText(lang);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? items.filter((item) => `${item.label} ${item.hint || ""}`.toLowerCase().includes(query)) : items;
  }, [items, search]);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected = filtered.length > 0 && filtered.every((item) => selected.has(item.id));

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };
  const toggleAll = () => {
    const next = new Set(selected);
    filtered.forEach((item) => (allSelected ? next.delete(item.id) : next.add(item.id)));
    onChange([...next]);
  };

  if (!items.length) {
    return (
      <p className="ca-picker-state" role="status">
        {emptyText}
      </p>
    );
  }

  return (
    <fieldset className="ca-multi" disabled={disabled}>
      <legend>{label}</legend>
      {items.length > 6 && (
        <Input
          aria-label={text.searchRestaurants}
          placeholder={text.searchRestaurants}
          value={search}
          iconStart={<Search size={16} />}
          onChange={(event) => setSearch(event.target.value)}
        />
      )}
      <div className="ca-select-bar">
        <Checkbox
          checked={allSelected}
          onChange={toggleAll}
          disabled={disabled || filtered.length === 0}
          label={text.selectAll(filtered.length)}
        />
        <span className="ca-selected-count" role="status">
          {text.selectedCount(selected.size)}
        </span>
      </div>
      <ul className="ca-multi-list">
        {filtered.map((item) => (
          <li key={item.id}>
            <Checkbox
              checked={selected.has(item.id)}
              onChange={() => toggle(item.id)}
              disabled={disabled}
              label={
                <span className="ca-multi-row">
                  <span className="ca-multi-name">{item.label}</span>
                  {item.hint && <span className="ca-multi-hint">{item.hint}</span>}
                  {item.status && <StatusBadge status={item.status} />}
                </span>
              }
            />
          </li>
        ))}
        {filtered.length === 0 && <li className="ca-multi-hint">{text.noMatches}</li>}
      </ul>
    </fieldset>
  );
}
